import { CatalogOverplot } from '@/modules/CatalogOverplotModule/CatalogOverplot';
import { store } from '@/store';
import { assert } from '@/utils/error';
import { V4 } from '@/utils/math';
import { range } from '@/utils/range';
import { EquatorialCoord } from '@hscmap/angle';
import { GlobePointerEvent } from '@hscmap/stellar-globe/dist/types/layer/point_event_layer';
import { Bridge } from '..';
import { ICallback } from './callback';

interface IAddCatalog {
  type: 'add_catalog'
  args: {
    id: string
    ra: number[]
    dec: number[]
    name: string
    columns: Array<[string, number[]]>
    color?: V4
    on_click: ICallback,
    on_change: ICallback,
  }
}

interface IRemoveCatalog {
  type: 'remove_catalog'
  args: {
    id: string,
  }
}

interface IUpdateCatalog {
  type: 'update_catalog'
  args: {
    id: string
    name: string | null
    color: V4 | null
    ra: number[] | null
    dec: number[] | null
    columns: Array<[string, number[]]>,
  }
}

function makeCO(name: string, ra: number[], dec: number[], columns: Array<[string, number[]]>) {
  const header = columns.map(([k, v]) => k)
  const rows = range(ra.length).map((i) => columns.map((column) => column[1][i]))
  const coord3 = range(ra.length).map((i) => EquatorialCoord.fromDeg(ra[i], dec[i]).xyz)
  return CatalogOverplot.make({ name, header, coord3, rows })
}

class JCatalog {
  constructor(
    readonly b: Bridge, readonly id: string,
    readonly onClick: ICallback, readonly onChange: ICallback,
    public co: CatalogOverplot,
  ) {
    this.setupCallbacks()
    this.cos.push(co)
  }

  private setupCallbacks() {
    this.cleanups.push(
      onPointerUp((e) => {
        const indices = this.co.spatialIndex.nearest(e.coord.xyz, 1, searchRadius())
        if (indices.length > 0) {
          this.b.callback(this.onClick, [indices[0]])
        }
      }),
      this.co.$watch(() => [this.co.name, this.co.color], () => {
        const { name, color } = this.co
        this.b.callback(this.onChange, [{ name, color }])
      }),
    )
  }

  remove() {
    this.clearCallbacks()
    const i = this.cos.indexOf(this.co)
    if (i >= 0) {
      this.cos.splice(i, 1)
    }
  }

  private cleanups: Array<() => void> = []
  private clearCallbacks() {
    while (this.cleanups.length > 0) {
      this.cleanups.pop()!()
    }
  }

  update(msg: IUpdateCatalog) {
    const { ra, dec, columns, color, name } = msg.args
    assert((!!ra === !!dec) && (!!ra === !!columns))
    if (ra) {
      this.clearCallbacks()
      const co = makeCO(this.co.name, ra, dec!, columns)
      co.color = this.co.color
      const i = this.cos.indexOf(this.co)
      if (i >= 0) {
        this.cos.splice(i, 1, co)
      } else {
        this.cos.push(co)
      }
      this.co = co
      this.setupCallbacks()
    }
    if (name !== null) {
      this.co.name = name
    }
    if (color) {
      this.co.color = color
    }
  }

  static get cos() {
    return store.currentFrame.modules.catalogOverPlot.cos
  }

  get cos() {
    return JCatalog.cos
  }

  static fromMessage(b: Bridge, msg: IAddCatalog) {
    const { id, ra, dec, columns, color, name, on_click: onClick, on_change: onChange } = msg.args
    const co = makeCO(name, ra, dec, columns)
    if (color) {
      co.color = color
    }
    return new JCatalog(b, id, onClick, onChange, co)
  }
}

const jCatalogs: { [id: string]: JCatalog } = {}

export function add_catalog(b: Bridge, msg: IAddCatalog) {
  const { id } = msg.args
  const jCat = JCatalog.fromMessage(b, msg)
  jCatalogs[id] = jCat
}

export function remove_catalog(b: Bridge, msg: IRemoveCatalog) {
  const { id } = msg.args
  const jCat = jCatalogs[id]
  delete jCatalogs[id]
  jCat && jCat.remove()
}

export function update_catalog(b: Bridge, msg: IUpdateCatalog) {
  const { id } = msg.args
  const jCat = jCatalogs[id]
  if (jCat) {
    jCat.update(msg)
  }
}

function onPointerUp(cb: (e: GlobePointerEvent) => void) {
  const globe = store.currentGlobe()
  globe.ee.on('pointer-up', cb)
  return () => {
    globe.ee.off('pointer-up', cb)
  }
}

function searchRadius() {
  const globe = store.currentGlobe()
  const { fovy } = globe.viewFactory
  const R = 96
  const H = globe.canvas.domElement.clientHeight
  return fovy * R / H
}
